import axios from "axios";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
export const BACKEND_URL = backendUrl;
export const API_BASE = backendUrl ? `${backendUrl}/api` : "/api";

const ACCESS_TOKEN_KEY = "rollcall_access_token";
const REFRESH_TOKEN_KEY = "rollcall_refresh_token";

export function getStoredAccessToken() {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getStoredRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setStoredTokens({ access_token, refresh_token }) {
  if (access_token) localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
  if (refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
}

export function clearStoredTokens() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = getStoredAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let refreshPromise = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (!original || original._retry) throw error;

    if (error.response?.status !== 401) throw error;

    const refreshToken = getStoredRefreshToken();
    if (!refreshToken) throw error;

    if (!refreshPromise) {
      refreshPromise = axios
        .post(`${API_BASE}/otp/refresh`, { refresh_token: refreshToken })
        .then((res) => {
          setStoredTokens(res.data);
          return res.data.access_token;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }

    try {
      const newAccess = await refreshPromise;
      original._retry = true;
      original.headers.Authorization = `Bearer ${newAccess}`;
      return api(original);
    } catch {
      clearStoredTokens();
      throw error;
    }
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function parseContentDispositionFilename(header) {
  if (!header) return null;
  const star = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (star) return decodeURIComponent(star[1]);
  const plain = header.match(/filename="([^"]+)"/i);
  return plain?.[1] ?? null;
}

/** Download session attendance as CSV (GET /sessions/:id/export). */
export async function downloadSessionAttendance(sessionId) {
  const token = getStoredAccessToken();
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const qs = tz ? `?tz=${encodeURIComponent(tz)}` : "";
  const res = await fetch(`${API_BASE}/sessions/${encodeURIComponent(sessionId)}/export${qs}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    let detail = "Download failed";
    try {
      const body = await res.json();
      detail = formatApiErrorDetail(body.detail);
    } catch {
      // ignore
    }
    throw new Error(detail);
  }

  const blob = await res.blob();
  const filename =
    parseContentDispositionFilename(res.headers.get("Content-Disposition")) ||
    `attendance_${String(sessionId).slice(0, 12)}.xlsx`;

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);

  return filename;
}
